/**
 * Cloud Save Manager — Supabase backend for web_save_games
 *
 * Handles saving/loading game state to Supabase.
 * Used by robustSaveManager as the primary save backend,
 * with localStorage as sync-safe fallback.
 *
 * Throttled to max 1 cloud write per 10 seconds.
 */

import { supabase } from './supabase.ts';
import { debug } from './debug.js';

const TABLE = 'web_save_games';
const THROTTLE_MS = 10_000; // 10 seconds between cloud saves

let lastCloudSaveTime = 0;
let pendingSave = null; // queued save waiting for throttle window

/**
 * Save world state to Supabase cloud.
 * Throttled — if called too frequently, queues the latest and writes when window opens.
 *
 * @param {string} userId - Supabase auth user ID
 * @param {Object} worldState - Complete game state
 * @param {number} slot - Save slot (default 1)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function cloudSave(userId, worldState, slot = 1) {
  if (!userId) {
    return { success: false, error: 'No user ID' };
  }

  const now = Date.now();
  const elapsed = now - lastCloudSaveTime;

  if (elapsed < THROTTLE_MS) {
    // Queue this save — it will fire when the throttle window opens
    if (!pendingSave) {
      const delay = THROTTLE_MS - elapsed;
      pendingSave = setTimeout(async () => {
        pendingSave = null;
        await doCloudSave(userId, worldState, slot);
      }, delay);
    }
    return { success: true, queued: true };
  }

  return await doCloudSave(userId, worldState, slot);
}

async function doCloudSave(userId, worldState, slot) {
  lastCloudSaveTime = Date.now();

  try {
    const { error } = await supabase
      .from(TABLE)
      .upsert({
        user_id: userId,
        save_slot: slot,
        world_state: worldState,
        day_number: worldState.dayNumber ?? null,
        cash: worldState.airline?.cash ?? null,
        airline_name: worldState.airline?.name ?? null,
        aircraft_count: worldState.aircraft?.length ?? 0,
        version: (worldState._saveVersion ?? 0) + 1,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,save_slot',
      });

    if (error) {
      console.error('[CloudSave] Upsert failed:', error.message);
      return { success: false, error: error.message };
    }

    debug('[CloudSave] Saved to cloud (slot', slot, ')');
    return { success: true };
  } catch (err) {
    console.error('[CloudSave] Error:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Load world state from Supabase cloud.
 *
 * @param {string} userId - Supabase auth user ID
 * @param {number} slot - Save slot (default 1)
 * @returns {Promise<{success: boolean, data?: Object, exists: boolean, error?: string}>}
 */
export async function cloudLoad(userId, slot = 1) {
  if (!userId) {
    return { success: false, data: null, exists: false, error: 'No user ID' };
  }

  try {
    const { data, error } = await supabase
      .from(TABLE)
      .select('world_state, updated_at')
      .eq('user_id', userId)
      .eq('save_slot', slot)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No row found — no cloud save exists
        return { success: true, data: null, exists: false };
      }
      console.error('[CloudSave] Load failed:', error.message);
      return { success: false, data: null, exists: false, error: error.message };
    }

    if (!data?.world_state) {
      return { success: true, data: null, exists: false };
    }

    debug('[CloudSave] Loaded from cloud (slot', slot, ', updated:', data.updated_at, ')');
    return { success: true, data: data.world_state, exists: true };
  } catch (err) {
    console.error('[CloudSave] Load error:', err);
    return { success: false, data: null, exists: false, error: err.message };
  }
}

/**
 * Check if a cloud save exists.
 *
 * @param {string} userId
 * @param {number} slot
 * @returns {Promise<boolean>}
 */
export async function cloudExists(userId, slot = 1) {
  if (!userId) return false;

  try {
    const { count, error } = await supabase
      .from(TABLE)
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('save_slot', slot);

    if (error) return false;
    return (count ?? 0) > 0;
  } catch {
    return false;
  }
}

/**
 * Delete a cloud save.
 *
 * @param {string} userId
 * @param {number} slot
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function cloudClear(userId, slot = 1) {
  if (!userId) return { success: false, error: 'No user ID' };

  try {
    const { error } = await supabase
      .from(TABLE)
      .delete()
      .eq('user_id', userId)
      .eq('save_slot', slot);

    if (error) {
      return { success: false, error: error.message };
    }

    debug('[CloudSave] Cleared cloud save (slot', slot, ')');
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/**
 * List all save slots with metadata (no full world_state).
 *
 * @param {string} userId
 * @returns {Promise<Array<{slot: number, dayNumber: number, cash: number, airlineName: string, updatedAt: string}>>}
 */
export async function cloudListSaves(userId) {
  if (!userId) return [];

  try {
    const { data, error } = await supabase
      .from(TABLE)
      .select('save_slot, day_number, cash, airline_name, aircraft_count, updated_at')
      .eq('user_id', userId)
      .order('save_slot', { ascending: true });

    if (error) return [];

    return (data ?? []).map(row => ({
      slot: row.save_slot,
      dayNumber: row.day_number,
      cash: row.cash,
      airlineName: row.airline_name,
      aircraftCount: row.aircraft_count,
      updatedAt: row.updated_at,
    }));
  } catch {
    return [];
  }
}

export default {
  save: cloudSave,
  load: cloudLoad,
  exists: cloudExists,
  clear: cloudClear,
  listSaves: cloudListSaves,
};
